import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import collegeService from '../services/collegeService';

// Async thunks
export const fetchColleges = createAsyncThunk(
  'colleges/fetchColleges',
  async (params, { rejectWithValue }) => {
    try {
      const response = await collegeService.getColleges(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch colleges');
    }
  }
);

export const createCollege = createAsyncThunk(
  'colleges/createCollege',
  async (collegeData, { rejectWithValue }) => {
    try {
      const response = await collegeService.createCollege(collegeData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create college');
    }
  }
);

export const updateCollege = createAsyncThunk(
  'colleges/updateCollege',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await collegeService.updateCollege(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update college');
    }
  }
);

export const deleteCollege = createAsyncThunk(
  'colleges/deleteCollege',
  async (id, { rejectWithValue }) => {
    try {
      await collegeService.deleteCollege(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete college');
    }
  }
);

const initialState = {
  list: [],
  current: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

const collegeSlice = createSlice({
  name: 'colleges',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentCollege: (state, action) => {
      state.current = action.payload;
    },
    clearCurrentCollege: (state) => {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Colleges
      .addCase(fetchColleges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchColleges.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.colleges;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchColleges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create College
      .addCase(createCollege.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCollege.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload.college);
      })
      .addCase(createCollege.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update College
      .addCase(updateCollege.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCollege.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(college => college._id === action.payload.college._id);
        if (index !== -1) {
          state.list[index] = action.payload.college;
        }
        state.current = action.payload.college;
      })
      .addCase(updateCollege.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete College
      .addCase(deleteCollege.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCollege.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(college => college._id !== action.payload);
      })
      .addCase(deleteCollege.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentCollege, clearCurrentCollege } = collegeSlice.actions;
export default collegeSlice.reducer;